import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Cropper from "react-easy-crop";
import api from "../../../config/URL";
import ImageURL from "../../../config/ImageURL";
import "../../../styles/admin.css";
import Select from "react-select";

function SliderEdit() {
  const [loadIndicator, setLoadIndicator] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalFileName, setOriginalFileName] = useState("");
  const [originalFileType, setOriginalFileType] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const SUPPORTED_FORMATS = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/svg+xml",
    "image/webp",
  ];

  useEffect(() => {
    // Fetch products
    api
      .get("admin/getAllProductList")
      .then((response) => {
        const productData = response.data.data || [];
        setProducts(
          productData.map((product) => ({
            value: product.value,
            label: product.label,
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      });

    // Fetch categories
    api
      .get("admin/getAllCategoriesList")
      .then((response) => {
        const categoryData = response.data.data || [];
        const formattedCategories = categoryData.map((category) => ({
          value: category.value,
          label: category.label,
        }));
        setCategories(formattedCategories);
        console.log("Categories:", formattedCategories); // Debug
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });

    // Fetch campaigns
    api.get("admin/getAllCampaignsList").then((response) => {
      const campaignData = response.data.data || [];
      setCampaigns(
        campaignData.map((campaign) => ({
          value: campaign.value,
          label: campaign.label,
        }))
      );
    });
  }, []);

  const imageValidation = Yup.mixed()
    .nullable()
    .test("fileFormat", "Unsupported format", (value) => {
      return !value || (value && SUPPORTED_FORMATS.includes(value.type));
    })
    .test("fileSize", "File size is too large. Max 2MB.", (value) => {
      return !value || (value && value.size <= MAX_FILE_SIZE);
    });

  const validationSchema = Yup.object({
    title: Yup.string().required("*Please fill a title field"),
    target_type: Yup.string().required("*Select a type"),
    order: Yup.string().required("*Select an Order"),
    image: imageValidation,
    target_id: Yup.string().when("target_type", {
      is: (value) => value && value !== "default",
      then: () => Yup.string().required("*Select a target"), // Use function
      otherwise: () => Yup.string().nullable(), // Use function
    }),
  });

  const formik = useFormik({
    initialValues: {
      order: "",
      image: null,
      target_type: "",
      title: "",
      target_id: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("order", values.order);
      formData.append("title", values.title);
      formData.append("target_type", values.target_type);

      if (values.target_type !== "default") {
        formData.append("target_id", values?.target_id);
      }

      if (values.image) {
        formData.append("image", values.image);
      }

      setLoadIndicator(true);
      try {
        const response = await api.post(`admin/slider/update/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/slider");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error updating the slider.";
        toast.error(errorMessage);
      }
      setLoadIndicator(false);
    },
  });

  const getOptions = () => {
    switch (formik.values.target_type) {
      case "product":
        return products;
      case "category":
        return categories;
      case "campaign":
        return campaigns;
      default:
        return [];
    }
  };

  const getSelectedValue = () => {
    if (!formik?.values?.target_id) return null;
    return getOptions().find(
      (option) =>
        option.value.toString() === formik?.values?.target_id.toString()
    );
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`admin/slider/${id}`);
        const sliderData = response.data.data;
        console.log("Slider Data:", sliderData); // Debug

        formik.setValues({
          order: sliderData.order || "",
          title: sliderData.title || "",
          target_type: sliderData.target_type || "",
          target_id: sliderData.target_id || "",
          image: null,
        });
        setPreviewImage(`${ImageURL}${sliderData.image_path}`);
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error Fetching Data";
        toast.error(errorMessage);
      }
      setLoading(false);
    };
    getData();
  }, [id]);

  const handleFileChange = (event) => {
    const file = event?.target?.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size is too large. Max 2MB.");
        event.target.value = null;
        formik.setFieldValue("image", null);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setOriginalFileName(file.name);
        setOriginalFileType(file.type);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImg = (imageSrc, crop, croppedAreaPixels) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const targetWidth = 900;
        const targetHeight = 400;
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.drawImage(
          image,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          targetWidth,
          targetHeight
        );

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          blob.name = "croppedImage.jpeg";
          resolve(blob);
        }, "image/jpeg");
      };
    });
  };

  const handleCropSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(
        imageSrc,
        crop,
        croppedAreaPixels
      );
      const fileName = originalFileName;

      const file = new File([croppedImageBlob], fileName, {
        type: originalFileType,
      });

      formik.setFieldValue("image", file);
      setOriginalFileType(file.type);

      const croppedImageURL = URL.createObjectURL(croppedImageBlob);
      setPreviewImage(croppedImageURL);
      setShowCropper(false);
    } catch (error) {
      console.error("Error cropping the image:", error);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    formik.setFieldValue("image", "");
    document.querySelector("input[type='file']").value = "";
  };

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow border-0 mb-3">
          <div className="row p-3">
            <div className="d-flex justify-content-between align-items-center">
              <h1 className="h4 ls-tight">Edit Slider</h1>
              <Link to="/slider">
                <button type="button" className="btn btn-light btn-sm">
                  <span>Back</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
        <div
          className="container card shadow border-0"
          style={{ minHeight: "60vh" }}
        >
          {loading ? (
            <div className="loader-container">
              <div className="loader">
                <svg viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="32"></circle>
                </svg>
              </div>
            </div>
          ) : (
            <>
              <div className="row py-4">
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    placeholder="Title"
                    className={`form-control ${
                      formik.touched.title && formik.errors.title
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("title")}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <div className="invalid-feedback">
                      {formik.errors.title}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Image
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".png,.jpeg,.jpg,.svg,.webp"
                    className={`form-control ${
                      formik.touched.image && formik.errors.image
                        ? "is-invalid"
                        : ""
                    }`}
                    name="image"
                    onChange={handleFileChange}
                    onBlur={formik.handleBlur}
                  />
                  <p style={{ fontSize: "13px" }}>
                    Note: Maximum file size is 2MB. Allowed: .png, .jpg, .jpeg,
                    .svg, .webp.
                  </p>
                  {formik.touched.image && formik.errors.image && (
                    <div className="invalid-feedback">
                      {formik.errors.image}
                    </div>
                  )}
                  {previewImage && (
                    <div className="my-3">
                      <img
                        src={previewImage}
                        alt="Selected"
                        style={{ maxWidth: "100px", maxHeight: "100px" }}
                      />
                    </div>
                  )}
                  {showCropper && imageSrc && (
                    <div className="crop-container">
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={900 / 400}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        cropShape="rect"
                        showGrid={false}
                      />
                    </div>
                  )}

                  {showCropper && (
                    <div className="d-flex justify-content-start mt-3 gap-2">
                      <button
                        type="button"
                        className="btn btn-primary mt-3"
                        onClick={handleCropSave}
                      >
                        Save Cropped Image
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3"
                        onClick={handleCropCancel}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">
                    Order<span className="text-danger">*</span>
                  </label>
                  <select
                    aria-label="Default select example"
                    className={`form-select ${
                      formik.touched.order && formik.errors.order
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("order")}
                  >
                    <option value="">Select an order</option>
                    {Array.from({ length: 8 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                  {formik.touched.order && formik.errors.order && (
                    <div className="invalid-feedback">
                      {formik.errors.order}
                    </div>
                  )}
                </div>
                <div className="col-md-6 col-12 file-input mb-5">
                  <label className="form-label">
                    Type<span className="text-danger">*</span>
                  </label>
                  <select
                    aria-label="Default select example"
                    className={`form-select ${
                      formik.touched.target_type && formik.errors.target_type
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("target_type")}
                    onChange={(e) => {
                      formik.handleChange(e);
                      formik.setFieldValue("target_id", "");
                    }}
                  >
                    <option value="default">Default</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="campaign">Campaign</option>
                  </select>
                  {formik.touched.target_type && formik.errors.target_type && (
                    <div className="invalid-feedback">
                      {formik.errors.target_type}
                    </div>
                  )}
                </div>

                {formik.values.target_type &&
                  formik.values.target_type !== "default" && (
                    <div className="col-md-6 col-12 file-input mb-5">
                      <label className="form-label">
                        {formik.values.target_type === "product" && "Product"}
                        {formik.values.target_type === "category" && "Category"}
                        {formik.values.target_type === "campaign" && "Campaign"}
                        <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={getOptions()}
                        value={getSelectedValue()}
                        onChange={(selectedOption) =>
                          formik.setFieldValue(
                            "target_id",
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        onBlur={formik.handleBlur}
                        name="target_id"
                        placeholder={`Search & select ${formik.values.target_type}...`}
                        isSearchable
                        className={`react-select ${
                          formik.touched.target_id && formik.errors.target_id
                            ? "is-invalid"
                            : ""
                        }`}
                        classNamePrefix="select"
                      />
                      {formik.touched.target_id && formik.errors.target_id && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.target_id}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <div className="hstack p-2 mt-5">
                <button
                  type="submit"
                  className="btn btn-sm btn-button"
                  disabled={loadIndicator}
                >
                  {loadIndicator && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    ></span>
                  )}
                  Update
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </section>
  );
}

export default SliderEdit;
